/**
 * Port of `AgentScheduler.mechanicalStance` from the Mac app — the first-match cascade that
 * turns one stock's data into a Buy / Watch / Hold / Sell badge.
 *
 * ORDER IS LOAD-BEARING. This is a cascade, not a scoring function: the first rule that matches
 * wins and the rest are never consulted. Reordering these branches silently changes the app's
 * behaviour, which is exactly why they're numbered here the same way they are in the Swift.
 *
 * Note what is deliberately NOT here: any notion of ranking, conviction, or "best ideas". Those
 * are Claude's judgement in the real app's Recommendations screen. This file only answers
 * "did a threshold trip?", which is a much narrower and more honest question.
 */

import { evaluate, dividendYieldFraction, pctAboveLow } from './entryScreen';
import type { DemoStock, Stance, StanceResult, Thresholds } from './types';

function pct(fraction: number | null, digits = 1): string {
  if (fraction === null) return '—';
  return `${(fraction * 100).toFixed(digits)}%`;
}

export function stanceFor(
  stock: DemoStock,
  isHeld: boolean,
  t: Thresholds,
): StanceResult {
  const yieldFraction = dividendYieldFraction(stock.annualDividendPerShare, stock.price);
  const aboveLow = pctAboveLow(stock.price, stock.fiftyTwoWeekLow);
  const belowSMA200 = stock.sma200 !== null ? stock.price < stock.sma200 : null;

  // 1. Held and below its 200-day average — the trend filter's own sell condition.
  //    The ONLY sell this engine produces. There is no valuation or overbought sell, which
  //    is a real limitation rather than an oversight: those need judgement, not a threshold.
  if (isHeld && t.trendFilterEnabled && belowSMA200 === true) {
    return {
      stance: 'sell',
      rationale: `Held and trading below its 200-day average (${stock.sma200!.toFixed(2)} vs ${stock.price.toFixed(2)}) — the trend filter flags this for review.`,
    };
  }

  const verdict = evaluate({
    pctAboveLowFraction: aboveLow,
    yieldFraction,
    enabled: t.entryScreenEnabled,
    nearLowThresholdFraction: t.nearLowFraction,
    minYieldFraction: t.minYieldFraction,
    maxYieldFraction: t.maxYieldFraction,
  });

  // 2. Clean entry-screen hit, not already held.
  if (!isHeld && verdict === 'qualifies') {
    return {
      stance: 'buy',
      rationale: `Meets the entry screen: yield ${pct(yieldFraction)}, ${pct(aboveLow, 0)} above its 52-week low.`,
    };
  }

  // 3a. Near the low and yielding, but above the sanity ceiling — flagged, never a clean buy.
  //     An unusually high yield is more often distress (a collapsed price, an unsustainable
  //     payout) than a bargain.
  if (!isHeld && verdict === 'highYieldFlagged') {
    return {
      stance: 'watch',
      rationale: `Near its 52-week low, but its ${pct(yieldFraction)} yield is above the ${pct(t.maxYieldFraction, 0)} sanity ceiling — verify it's sustainable before treating this as a buy.`,
    };
  }

  // 3b. Near the low without clearing the screen — worth watching, not buying.
  //     Note this shares the SAME near-low test as the buy branch; the only difference
  //     between Buy and Watch here is the yield condition.
  const nearLow = aboveLow !== null && aboveLow >= 0 && aboveLow <= t.nearLowFraction;
  if (!isHeld && nearLow) {
    const why = yieldFraction === null
      ? 'it pays no dividend we can measure'
      : `its ${pct(yieldFraction)} yield is below the ${pct(t.minYieldFraction, 0)} minimum`;
    return {
      stance: 'watch',
      rationale: `Near its 52-week low (${pct(aboveLow, 0)} above), but doesn't clear the entry screen — ${why}.`,
    };
  }

  // 4. Everything else.
  return {
    stance: 'hold',
    rationale: isHeld
      ? 'Held — no rule triggered today.'
      : 'No rule triggered today.',
  };
}

/** Display order for grouping rows: actionable first, quiet last. */
export const STANCE_ORDER: Record<Stance, number> = {
  buy: 0, sell: 1, watch: 2, hold: 3,
};

export function sortByStance(
  rows: Array<{ stance: Stance; ticker: string }>,
): typeof rows {
  return [...rows].sort((a, b) =>
    STANCE_ORDER[a.stance] - STANCE_ORDER[b.stance] || a.ticker.localeCompare(b.ticker));
}
